import { createFileRoute } from '@tanstack/react-router';
import { ProfileVerification } from '../-components/ProfileVerification';
import {
  Badge,
  Button,
  FormField,
  ListTable,
  ListTableCell,
  ListTableHeader,
  ListTableRow,
  RadioGroup,
  RadioGroupItem,
  Separator,
} from '@opaz/ui-components';
import { formatIsoDate } from '@/lib/utils';
import { useContext, useMemo, useState } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { queryClient } from '@/services/queryClient';
import { updateUserProfileDetails } from '@/api/backend/profile';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ProfileContext } from '../route';
import { InvestorProfileType } from '@/api/backend/types';

export const Route = createFileRoute('/_auth/profile/investor/_layout/personal-information')({
  component: RouteComponent,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
});

function RouteComponent() {
  const { profile } = useContext(ProfileContext);
  const { t, i18n } = useTranslation();
  const investorProfile = profile as InvestorProfileType;

  const [isRequireROPVerification, setIsRequireROPVerification] = useState<boolean>(
    investorProfile.personalDetails?.resident && investorProfile.personalDetails?.status !== 'VERIFIED',
  );

  // TODO: Remove the isPkiLoginV2 check when the new pki login is released
  const isPkiLoginV2 = useMemo(() => {
    return localStorage.getItem('pkiLogin') === 'v2';
  }, []);

  const { mutateAsync: updateNotificationSettings, isPending: isSaveLoading } = useMutation({
    mutationFn: async (data: { languagePreference: string }) => {
      await updateUserProfileDetails({
        phoneNumber: investorProfile.notificationDetails.mobileNumber,
        emailAddress: investorProfile.notificationDetails.email,
        languagePreference: data.languagePreference,
      });
    },
  });

  const { control, formState, handleSubmit, reset } = useForm<{ languagePreference: string }>({
    disabled: isSaveLoading,
    values: {
      languagePreference: investorProfile.languagePreference.toLowerCase(),
    },
  });

  const { field: languagePreferenceField } = useController({
    control,
    name: 'languagePreference',
  });

  const onSubmit: SubmitHandler<{ languagePreference: string }> = async ({ languagePreference }) => {
    await updateNotificationSettings({ languagePreference });
    i18n.changeLanguage(languagePreference);
    reset({ languagePreference });

    queryClient.invalidateQueries({
      queryKey: ['profileDetails', investorProfile.id],
    });
  };

  return (
    <div>
      {isRequireROPVerification && !isPkiLoginV2 && (
        <>
          <ProfileVerification profile={investorProfile} onSuccess={() => setIsRequireROPVerification(false)} />

          <Separator type="medium" className="my-12" />
        </>
      )}

      <h1 className="text-foreground text-3xl font-bold mb-4">
        {t('profile.investor.tabs.personalInformation.personalInformationSection.title')}
      </h1>

      <ListTable>
        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.englishName')}
          </ListTableHeader>
          <ListTableCell>{investorProfile?.personalDetails.fullNameEn}</ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.arabicName')}
          </ListTableHeader>
          <ListTableCell>{investorProfile?.personalDetails.fullNameAr}</ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.civilNumber')}
          </ListTableHeader>
          <ListTableCell>{investorProfile?.personalDetails.civilNumber}</ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.idCardExpiration')}
          </ListTableHeader>
          <ListTableCell>
            {formatIsoDate(investorProfile?.personalDetails.idCardExpiration, i18n.language)}
          </ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.emailAddress')}
          </ListTableHeader>
          <ListTableCell>{investorProfile?.personalDetails.email}</ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.phoneNumber')}
          </ListTableHeader>
          <ListTableCell>{investorProfile?.personalDetails.phoneNumber}</ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.nationality')}
          </ListTableHeader>
          <ListTableCell>
            {i18n.language === 'ar'
              ? investorProfile?.personalDetails.nationalityNameAr
              : investorProfile?.personalDetails.nationalityNameEn}
          </ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.gender')}
          </ListTableHeader>
          <ListTableCell>
            {investorProfile?.personalDetails.gender
              ? t(`common.gender.${investorProfile?.personalDetails.gender}`)
              : '-'}
          </ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.dateOfBirth')}
          </ListTableHeader>
          <ListTableCell>{formatIsoDate(investorProfile?.personalDetails.dateOfBirth, i18n.language)}</ListTableCell>
        </ListTableRow>

        <ListTableRow>
          <ListTableHeader>
            {t('profile.investor.tabs.personalInformation.personalInformationSection.listTable.verificationStatus')}
          </ListTableHeader>
          <ListTableCell>
            <Badge type={investorProfile.personalDetails.status === 'VERIFIED' ? 'positive' : 'warning'} filled>
              {investorProfile.personalDetails.status === 'VERIFIED'
                ? t('profile.verificationStatus.verified')
                : t('profile.verificationStatus.notVerified')}
            </Badge>
          </ListTableCell>
        </ListTableRow>
      </ListTable>

      <Separator type="medium" className="my-12" />

      <h2 className="text-foreground text-3xl font-bold mb-4">
        {t('profile.investor.tabs.personalInformation.preferredLanguageSection.title')}
      </h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormField className="mb-12">
          <RadioGroup
            dir={i18n.dir()}
            size="small"
            value={languagePreferenceField.value}
            onValueChange={languagePreferenceField.onChange}
            disabled={languagePreferenceField.disabled}
          >
            <RadioGroupItem id="option-1" value="en">
              English
            </RadioGroupItem>

            <RadioGroupItem id="option-2" value="ar">
              العربية
            </RadioGroupItem>
          </RadioGroup>
        </FormField>

        {formState.isDirty && (
          <div className="flex gap-3">
            <Button type="submit" disabled={isSaveLoading}>
              {t('common.save')}
            </Button>

            <Button variant="secondary" type="button" disabled={isSaveLoading} onClick={() => reset(undefined)}>
              {t('common.cancel')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
